import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, List, X, ArrowUpRight, House, Sparkle, ArrowRight, Translate, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { EMAILS } from "@/lib/emails";
import GlobalSearch from "@/components/GlobalSearch";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import DisplayControls from "@/components/DisplayControls";
import GeoBanner from "@/components/GeoBanner";
import { useLanguage, langLabel } from "@/context/LanguageContext";
import { EDITION, isIndia, isEdu } from "@/lib/edition";

const CO = EDITION.country ? `?country=${encodeURIComponent(EDITION.country)}` : "";

const PRIMARY = isEdu ? [
  { label: "Institutions", to: `/explore/institutions${CO}` },
  { label: "People", to: "/find-teacher" },
  { label: "Careers", to: "/jobs" },
  { label: "Alumni", to: "/alumni-groups" },
  { label: "Workmate Alumni", to: "/workmate" },
  { label: "Community", to: `/events${CO}` },
] : [
  { label: "About", to: "/about" },
  { label: isIndia ? "States" : "Countries", to: isIndia ? "/countries/India" : "/countries" },
  { label: "Institutions", to: `/explore/institutions${CO}` },
  { label: "Alumni Groups", to: "/alumni-groups" },
  { label: "Reunions", to: `/events${CO}` },
  { label: "Create a Group", to: "/groups" },
];

const SECONDARY = isEdu ? [
  { label: "Why IndianEduPulse", to: "/why-alumnipulse" },
  { label: "Exams", to: "/exams" },
  { label: "Scholarships", to: "/scholarships" },
  { label: "Fields of Study", to: "/disciplines" },
  { label: "In Memoriam", to: "/memorials" },
  { label: "Memory Lane", to: "/memory-lane" },
  { label: "Education News", to: `/news${CO}` },
  { label: "Articles", to: "/articles" },
  { label: "Support", to: "/support" },
  { label: "Contact", to: "/contact" },
] : [
  { label: isIndia ? "Why IndiaAlumni" : "Why AlumniPulse", to: "/why-alumnipulse" },
  { label: "In Memoriam", to: "/memorials" },
  { label: "Alumni Impact", to: "/impact" },
  { label: "Memory Lane", to: "/memory-lane" },
  { label: "News", to: `/news${CO}` },
  { label: "Articles", to: "/articles" },
  { label: "Trending Today", to: "/trending" },
  { label: "Support", to: "/support" },
  { label: "Contact", to: "/contact" },
];

const ALL_NAV = [{ label: "Home", to: "/" }, ...PRIMARY, ...SECONDARY];
const tid = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

const EXTERNAL = [
  { label: "SocialPulse", href: "https://socialpulse.com", mark: "®" },
  { label: "FoodPulse", href: "https://foodpulse.net", mark: "™" },
];

const LEGAL = [
  { label: "Terms of Service", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Cookie Policy", to: "/cookie" },
  { label: "Memorial & Obituary Policy", to: "/memorial-policy" },
];

const PULSE_FAMILY = [
  { label: "AlumniPulse", mark: "™", href: "https://alumnipulse.net", desc: "Alumni network" },
  { label: "SocialPulse", mark: "®", href: "https://socialpulse.com", desc: "Community platform" },
  { label: "FoodPulse", mark: "™", href: "https://foodpulse.net", desc: "Food & community" },
  { label: "TravelerPulse", mark: "™", href: "https://travelerpulse.pro", desc: "Travel community" },
];

export default function PublicLayout({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { lang, resetToEnglish, t, translating } = useLanguage();
  const active = (to) => { const p = to.split("?")[0]; return p === "/" ? pathname === "/" : pathname.startsWith(p); };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {isIndia && lang !== "en" && (
        <div data-testid="lang-reset-strip"
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-primary px-4 py-2 text-center text-xs font-medium text-primary-foreground sm:text-sm">
          <span>Reading IndiaAlumni in <span className="font-semibold">{langLabel(lang)}</span></span>
          {translating && (
            <span className="inline-flex items-center gap-1 opacity-90" data-testid="lang-translating">
              <CircleNotch size={13} className="animate-spin" /> translating…
            </span>
          )}
          <button onClick={resetToEnglish} data-testid="reset-to-english-btn"
            className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 font-semibold transition-colors hover:bg-white/30">
            <Translate size={13} weight="bold" /> Show in English
          </button>
        </div>
      )}
      {isIndia ? (
        <GeoBanner fallback={null} />
      ) : isEdu ? null : (
        <GeoBanner fallback={
          <Link to="/why-alumnipulse" data-testid="why-topbar-public"
            className="group flex items-center justify-center gap-2 bg-primary px-4 py-2 text-center text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:text-sm">
            <Sparkle size={14} weight="fill" className="text-secondary" />
            <span>Why AlumniPulse — the joy of finding your way back</span>
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        } />
      )}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5">
          <div className="flex h-16 items-center justify-between gap-4">
            <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="nav-logo">
              {isEdu ? (
                <>
                  <img src={EDITION.logo} alt={EDITION.brand} className="h-9 w-9 shrink-0 rounded-lg object-contain" />
                  <span className="flex flex-col leading-none">
                    <span className="font-heading text-lg font-bold tracking-tight text-[#1e3a5f]">IndianEdu<span className="text-[#f97316]">Pulse</span><span className="align-top text-[10px] font-semibold text-muted-foreground">.com</span></span>
                    <span className="mt-0.5 hidden text-[10px] text-muted-foreground lg:block">Discover India's Education. Connect. Remember. Grow.</span>
                  </span>
                </>
              ) : (
                <>
                  {isIndia
                    ? <img src={EDITION.logo} alt={EDITION.brand} className="h-9 w-9 shrink-0 rounded-lg object-contain" />
                    : <span className="relative block h-9 w-11 shrink-0 overflow-hidden"><img src="/alumnipulse-logo.png" alt="AlumniPulse" className="absolute left-1/2 top-0 max-w-none -translate-x-1/2" style={{ height: "66px" }} /></span>}
                  <span className="font-heading text-lg font-semibold tracking-tight">{EDITION.brand}{EDITION.mark}</span>
                </>
              )}
            </Link>

            <nav className="hidden min-w-0 flex-1 items-center justify-start gap-0.5 overflow-hidden pl-2 xl:flex" data-testid="public-nav">
              <Link to="/" aria-label="Home" data-testid="nav-home"
                className={`grid h-9 w-9 place-items-center rounded-full transition-colors ${active("/") ? "bg-accent text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                <House size={18} weight={active("/") ? "fill" : "regular"} />
              </Link>
              {PRIMARY.map((n) => (
                <Link key={n.label} to={n.to} data-testid={`nav-${tid(n.label)}`}
                  className={`whitespace-nowrap rounded-full px-2.5 py-1.5 text-sm transition-colors ${active(n.to) ? "bg-accent font-medium text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                  {t(n.label)}
                </Link>
              ))}
            </nav>

            <div className="hidden shrink-0 items-center gap-1.5 xl:flex">
              <DisplayControls />
              <LanguageSwitcher />
              <GlobalSearch />
              {!isEdu && <a href={EDITION.other.url} target="_blank" rel="noreferrer" data-testid="nav-other-edition"
                className="hidden items-center gap-0.5 whitespace-nowrap text-sm text-muted-foreground hover:text-secondary 2xl:flex">
                {EDITION.other.brand}<ArrowUpRight size={12} />
              </a>}
              {!isIndia && !isEdu && EXTERNAL.map((e) => (
                <a key={e.label} href={e.href} target="_blank" rel="noreferrer"
                  className="hidden items-center gap-0.5 whitespace-nowrap text-sm text-muted-foreground hover:text-secondary 2xl:flex" data-testid={`nav-ext-${e.label.toLowerCase()}`}>
                  {e.label}<sup className="text-[9px]">{e.mark}</sup>
                </a>
              ))}
              <span className="mx-1 hidden h-5 w-px bg-border 2xl:block" />
              {user
                ? <Link to="/dashboard"><Button size="sm" className="rounded-full">{t("Dashboard")}</Button></Link>
                : <>
                    <Link to="/login"><Button variant="ghost" size="sm">{t("Sign in")}</Button></Link>
                    <Link to="/register"><Button size="sm" className="rounded-full">{t("Get started")}</Button></Link>
                  </>}
            </div>

            {/* mobile */}
            <div className="flex min-w-0 items-center gap-1 xl:hidden">
              <LanguageSwitcher variant="mobile" />
              <span className="hidden sm:block"><GlobalSearch /></span>
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild><Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><List size={22} /></Button></SheetTrigger>
                <SheetContent side="right" className="w-80 overflow-y-auto">
                  <div className="mb-6 flex items-center gap-2">
                    {isIndia
                      ? <img src={EDITION.logo} alt={EDITION.brand} className="h-8 w-8 shrink-0 rounded-lg object-contain" />
                      : <span className="relative block h-8 w-10 shrink-0 overflow-hidden"><img src="/alumnipulse-logo.png" alt="AlumniPulse" className="absolute left-1/2 top-0 max-w-none -translate-x-1/2" style={{ height: "58px" }} /></span>}
                    <span className="font-heading font-semibold">{EDITION.brand}{EDITION.mark}</span>
                  </div>
                  <div className="mb-3"><GlobalSearch trigger="full" /></div>
                  <div className="mb-3 flex justify-center"><DisplayControls /></div>
                  <div className="flex flex-col gap-1">
                    {ALL_NAV.map((n) => (
                      <SheetClose asChild key={n.label}>
                        <Link to={n.to} className={`rounded-lg px-3 py-2 text-sm ${active(n.to) ? "bg-accent font-medium text-primary" : "text-muted-foreground"}`}>{t(n.label)}</Link>
                      </SheetClose>
                    ))}
                    <div className="my-2 h-px bg-border" />
                    {isIndia ? (
                      <a href={EDITION.other.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-2 text-sm text-secondary">
                        Visit {EDITION.other.label} (global) <ArrowUpRight size={13} />
                      </a>
                    ) : EXTERNAL.map((e) => (
                      <a key={e.label} href={e.href} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground">
                        {e.label}<sup className="text-[9px]">{e.mark}</sup> <ArrowUpRight size={13} />
                      </a>
                    ))}
                    <div className="my-2 h-px bg-border" />
                    {user
                      ? <SheetClose asChild><Link to="/dashboard"><Button className="w-full rounded-full">Dashboard</Button></Link></SheetClose>
                      : <div className="flex flex-col gap-2">
                          <SheetClose asChild><Link to="/login"><Button variant="outline" className="w-full rounded-full">Sign in</Button></Link></SheetClose>
                          <SheetClose asChild><Link to="/register"><Button className="w-full rounded-full">Get started</Button></Link></SheetClose>
                        </div>}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
          <div className="hidden justify-center gap-1 pb-2 lg:flex" data-testid="public-nav-secondary">
            {SECONDARY.map((n) => (
              <Link key={n.label} to={n.to} data-testid={`nav-${tid(n.label)}`}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-[13px] transition-colors ${active(n.to) ? "bg-accent font-medium text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                {t(n.label)}
              </Link>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border/60 bg-muted/30">
        <div className="mx-auto max-w-7xl px-5 py-10">
          <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
            <Link to="/" className="flex items-center gap-2">
              {isIndia || isEdu
                ? <><img src={EDITION.logo} alt={EDITION.brand} className="h-12 w-12 rounded-lg object-contain" /><span className="font-heading text-lg font-semibold">{EDITION.brand}{EDITION.mark}</span></>
                : <img src="/alumnipulse-logo.png" alt="AlumniPulse.net" className="h-14 w-auto" />}
            </Link>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              <Link to="/founder" className="text-muted-foreground hover:text-foreground" data-testid="footer-founder">{t("Meet the Founder")}</Link>
              <Link to="/install" className="flex items-center gap-1 font-medium text-secondary hover:underline" data-testid="footer-install">{t("Get the App")}<ArrowUpRight size={13} /></Link>
              {LEGAL.map((l) => (
                <Link key={l.to} to={l.to} className="text-muted-foreground hover:text-foreground" data-testid={`footer-${l.to.slice(1)}`}>{t(l.label)}</Link>
              ))}
              {isIndia ? (
                <a href={EDITION.other.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-secondary hover:underline" data-testid="footer-global-link">
                  {t("Visit")} {EDITION.other.label} ({t("global")})<ArrowUpRight size={13} />
                </a>
              ) : EXTERNAL.map((e) => (
                <a key={e.label} href={e.href} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 text-muted-foreground hover:text-secondary">
                  {e.label}<sup className="text-[9px]">{e.mark}</sup>
                </a>
              ))}
            </div>
          </div>
          {!isIndia && (
          <div className="mt-8 border-t border-border/60 pt-6" data-testid="pulse-family">
            <p className="text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">Part of the Pulse family</p>
            <div className="mx-auto mt-4 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
              {PULSE_FAMILY.map((p) => (
                <a key={p.label} href={p.href} target="_blank" rel="noreferrer"
                  className="group rounded-xl border border-border/60 bg-background px-3 py-2.5 text-center transition-colors hover:border-secondary/40 hover:bg-accent/40"
                  data-testid={`pulse-family-${p.label.toLowerCase()}`}>
                  <span className="block text-sm font-medium text-foreground group-hover:text-secondary">{p.label}<sup className="text-[9px]">{p.mark}</sup></span>
                  <span className="block text-[11px] text-muted-foreground">{p.desc}</span>
                </a>
              ))}
            </div>
          </div>
          )}
          <p className="mt-8 text-center text-xs text-muted-foreground">{EDITION.copyright}</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-muted-foreground" data-testid="footer-emails">
            <a href={`mailto:${EMAILS.info}`} className="hover:text-secondary">{EMAILS.info}</a>
            <a href={`mailto:${EMAILS.contact}`} className="hover:text-secondary">{EMAILS.contact}</a>
            <a href={`mailto:${EMAILS.privacy}`} className="hover:text-secondary">{EMAILS.privacy}</a>
            <a href={`mailto:${EMAILS.abuse}`} className="hover:text-secondary">{EMAILS.abuse}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
